// ** React Imports
import { useState, SyntheticEvent, Fragment, ReactNode } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { styled, Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MuiMenu, { MenuProps } from '@mui/material/Menu'
import MuiMenuItem, { MenuItemProps } from '@mui/material/MenuItem'
import Typography, { TypographyProps } from '@mui/material/Typography'
import { Link } from 'react-router-dom'
import { useNavigate } from "react-router-dom";

// ** Icons Imports
import BellOutline from 'mdi-material-ui/BellOutline'

// ** Third Party Components
import PerfectScrollbarComponent from 'react-perfect-scrollbar'

// ** Custom Components Imports
import CustomChip from 'src/@core/components/mui/chip'
import CustomAvatar from 'src/@core/components/mui/avatar'

// ** Type Imports
import { Settings } from 'src/@core/context/settingsContext'
import { CustomAvatarProps } from 'src/@core/components/mui/avatar/types'
import { AlertState } from "src/types/store/storeTypes"
import { useSelector, useDispatch } from 'react-redux';
import { markAlertAsRead } from 'src/store/apps/alert'
import { RootState, AppDispatch } from 'src/store'
import Badge from '@mui/material/Badge';

interface Props {
    settings: Settings
}

// ** Styled Menu component
const Menu = styled(MuiMenu)<MenuProps>(({ theme }) => ({
    '& .MuiMenu-paper': {
        width: 380,
        overflow: 'hidden',
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    '& .MuiMenu-list': {
        padding: 0
    }
}))

// ** Styled MenuItem component
const MenuItem = styled(MuiMenuItem)<MenuItemProps>(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`
}))

const styles = {
    maxHeight: 344
}

// ** Styled PerfectScrollbar component
const PerfectScrollbar = styled(PerfectScrollbarComponent)({
    ...styles
})

// ** Styled Avatar component
const Avatar = styled(CustomAvatar)<CustomAvatarProps>({
    width: '2.375rem',
    height: '2.375rem',
    fontSize: '1.125rem'
})

// ** Styled component for the title in MenuItems
const MenuItemTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
    fontWeight: 600,
    flex: '1 1 100%',
    overflow: 'hidden',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    marginBottom: theme.spacing(0.75)
}))

// ** Styled component for the subtitle in MenuItems
const MenuItemSubtitle = styled(Typography)<TypographyProps>({
    flex: '1 1 100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
})

const NotificationDropdown = (props: Props) => {
    // ** Props
    const { settings } = props

    const alert = useSelector((state: AlertState) => state.alert);
    const dispatch = useDispatch<AppDispatch>();

    const navigate = useNavigate();

    const navigateToAlerts = () => {
        navigate("/alertlist");
    }

    // ** States
    const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null)

    // ** Hook
    const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

    // ** Vars
    const { direction } = settings

    const handleDropdownOpen = (event: SyntheticEvent) => {
        setAnchorEl(event.currentTarget)
    }

    const markAlertStatus = (alertId: string) => {
        dispatch(markAlertAsRead({ alertId: alertId }));
        handleDropdownClose();
    }

    const handleDropdownClose = () => {
        setAnchorEl(null)
    }


    return (
        <Fragment>
            <IconButton color='inherit' aria-haspopup='true' onClick={handleDropdownOpen} aria-controls='customized-menu'>
                <Badge badgeContent={alert.alerts.filter(alert => alert.isRead === false).length} color="primary">
                    <BellOutline />
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleDropdownClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
            >
                <MenuItem disableRipple sx={{ cursor: 'default', userSelect: 'auto', backgroundColor: 'transparent !important' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography sx={{ fontWeight: 600 }}>Notifications</Typography>
                        <CustomChip
                            skin='light'
                            size='small'
                            label={`${alert.alerts.filter(alert => alert.isRead === false).length} New`}
                            color='primary'
                            sx={{ height: 20, fontSize: '0.75rem', fontWeight: 500, borderRadius: '10px' }}
                        />
                    </Box>
                </MenuItem>
                <Box sx={{ maxHeight: 344, overflowY: 'auto', overflowX: 'hidden' }}>
                    {alert.alerts.map((alert) => (
                        <Box
                            onClick={() => markAlertStatus(alert.alertGuid)}
                            sx={{
                                cursor: 'pointer',
                                paddingTop: theme => theme.spacing(3),
                                paddingBottom: theme => theme.spacing(3),
                                paddingLeft: theme => theme.spacing(2),
                                paddingRight: theme => theme.spacing(2),
                                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                    backgroundColor: theme => theme.palette.action.hover,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                component={Link}
                                to={alert.link}
                                style={{ textDecoration: 'none' }}
                            >
                                <Avatar alt="Flora" src="/images/avatars/4.png" />
                                <Box
                                    sx={{
                                        mx: 4,
                                        flex: '1 1',
                                        display: 'flex',
                                        overflow: 'hidden',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <MenuItemTitle>{alert.title}</MenuItemTitle>
                                    <MenuItemSubtitle variant="body2">
                                        {alert.message}
                                    </MenuItemSubtitle>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                    {alert.createdDateString}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
                <MenuItem
                    disableRipple
                    sx={{ py: 3.5, borderBottom: 0, borderTop: theme => `1px solid ${theme.palette.divider}` }}
                >
                    <Button fullWidth variant='contained' onClick={() => {
                        handleDropdownClose();
                        navigateToAlerts();
                    }}>
                        Read All Notifications
                    </Button>
                </MenuItem>
            </Menu>
        </Fragment>
    )
}

export default NotificationDropdown
